import { Component, ElementRef, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { DealService } from 'src/app/private/services/deals.service';
import { LeadService } from 'src/app/private/services/lead.service';
import { PipeLineService } from 'src/app/private/services/pipeline.service';
import { UsersService } from 'src/app/private/services/users.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { PermissionService } from '../../services/permission.service';
import { buildTenantScopeParams } from 'src/app/utils/tenant-scope.util';

interface PipelineStatusCard {
  stage_name: string;
  count: number | string;
}

type AnalyticsTab = 'lead' | 'deals' | '360_ai';

@Component({
  selector: 'app-analytics',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.scss']
})
export class AnalyticsComponent {
  @ViewChild('metricsScroll') metricsScroll?: ElementRef<HTMLElement>;
  @ViewChild('dealsMetricsScroll') dealsMetricsScroll?: ElementRef<HTMLElement>;

  leadsAnalytics: any = {};
  currentTab: AnalyticsTab = '360_ai';
  leadLoading = true;
  totalLeads = 0;
  dealsAnalytics: any = {};
  dealsAnalyticsLoading = false;
  currencyCode: any = 'INR';
  isAdmin = false;
  isUserLoading = true;
  users: any = [];
  selectedUser: any = '';
  selectedYear: any = new Date().getFullYear();
  years: any[] = ['2020', '2021', '2022', '2023', '2024', '2025'];
  leadPipelineData: any[] = [];
  dealPipelineData: any[] = [];
  selectedLeadPipelineId: number | string = '';
  selectedDealPipelineId: number | string = '';
  isPipelineLoading = true;
  statusCards: PipelineStatusCard[] = [];
  dealStatusCards: PipelineStatusCard[] = [];

  accessObject: any = {};

  constructor(
    private api: LeadService,
    private dealsApi: DealService,
    private pipelineApi: PipeLineService,
    private localStorage: LocalStorageService,
    private userApi: UsersService,
    private permissionService: PermissionService
  ) {
    this.isAdmin = this.localStorage.checkFullAccessForTableList(
      this.localStorage.getRole()
    );

    this.accessObject = this.permissionService.getAccessObject('analytics');
    this.currentTab = '360_ai';
  }

  get activePipelineData(): any[] {
    return this.currentTab === 'deals' ? this.dealPipelineData : this.leadPipelineData;
  }

  get selectedPipelineId(): number | string {
    return this.currentTab === 'deals'
      ? this.selectedDealPipelineId
      : this.selectedLeadPipelineId;
  }

  set selectedPipelineId(value: number | string) {
    if (this.currentTab === 'deals') {
      this.selectedDealPipelineId = value;
    } else {
      this.selectedLeadPipelineId = value;
    }
  }

  get showPipelineFilter(): boolean {
    if (this.currentTab === '360_ai') return false;
    return (
      (this.currentTab === 'lead' && !!this.accessObject?.lead_view) ||
      (this.currentTab === 'deals' && !!this.accessObject?.deal_view)
    );
  }

  get showAnalyticsFilters(): boolean {
    return this.currentTab !== '360_ai';
  }

  fnChangeTab(event: AnalyticsTab) {
    this.currentTab = event;
    if (event === 'lead') {
      this.fnGetLeadAnalytics();
    } else if (event === 'deals') {
      this.fnGetDealsAnalytics();
    }
  }

  ngOnInit() {
    this.loadAllPipelines();
    this.getAllUsers();
    this.currencyCode = this.localStorage.getClientCurrency();

    const currentYear = new Date().getFullYear();
    if (!this.years.includes(currentYear.toString())) {
      this.years.push(currentYear.toString());
    }
  }

  loadAllPipelines() {
    const clientId = this.localStorage.getClientId();
    this.isPipelineLoading = true;

    forkJoin({
      lead: this.pipelineApi.getAllPipeLine({
        client_id: clientId,
        pipeline_type: 'lead',
      }),
      deal: this.pipelineApi.getAllPipeLine({
        client_id: clientId,
        pipeline_type: 'deal',
      }),
    }).subscribe({
      next: ({ lead, deal }: any) => {
        this.leadPipelineData = lead || [];
        this.dealPipelineData = deal || [];
        this.selectedLeadPipelineId = this.resolveDefaultPipelineId(this.leadPipelineData);
        this.selectedDealPipelineId = this.resolveDefaultPipelineId(this.dealPipelineData);
        this.isPipelineLoading = false;

        if (this.accessObject?.lead_view) {
          this.fnGetLeadAnalytics();
        } else if (this.accessObject?.deal_view) {
          this.fnGetDealsAnalytics();
        }
      },
      error: (e: any) => {
        console.error(e);
        this.isPipelineLoading = false;
        if (this.accessObject?.lead_view) {
          this.fnGetLeadAnalytics();
        } else if (this.accessObject?.deal_view) {
          this.fnGetDealsAnalytics();
        }
      },
    });
  }

  private resolveDefaultPipelineId(pipelines: any[]): number | string {
    const clientId = this.localStorage.getClientId();

    const clientStandard = pipelines.find(
      (pipeline) =>
        pipeline.client_id === clientId && pipeline.pipeline_name === 'Standard'
    );
    if (clientStandard?.pipeline_id) {
      return clientStandard.pipeline_id;
    }

    const namedStandard = pipelines.find(
      (pipeline) => pipeline.pipeline_name === 'Standard'
    );
    if (namedStandard?.pipeline_id) {
      return namedStandard.pipeline_id;
    }

    const clientDefault = pipelines.find(
      (pipeline) => pipeline.client_id === clientId && pipeline.set_default
    );
    if (clientDefault?.pipeline_id) {
      return clientDefault.pipeline_id;
    }

    const clientPipeline = pipelines.find(
      (pipeline) => pipeline.client_id === clientId
    );

    return clientPipeline?.pipeline_id ?? pipelines[0]?.pipeline_id ?? '';
  }

  fnChangePipeline(event: any) {
    this.selectedPipelineId = event;
    if (this.currentTab === 'deals') {
      this.fnGetDealsAnalytics();
    } else {
      this.fnGetLeadAnalytics();
    }
  }

  scrollMetrics(direction: 'left' | 'right', type: AnalyticsTab = 'lead') {
    const el =
      type === 'deals'
        ? this.dealsMetricsScroll?.nativeElement
        : this.metricsScroll?.nativeElement;

    if (!el) {
      return;
    }

    const scrollAmount = 220;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }

  private buildStatusCards(
    analytics: any,
    pipelineData: any[],
    selectedPipelineId: number | string
  ): PipelineStatusCard[] {
    if (Array.isArray(analytics?.status_counts) && analytics.status_counts.length) {
      return analytics.status_counts;
    }

    const selectedPipeline = pipelineData.find(
      (pipeline) => String(pipeline.pipeline_id) === String(selectedPipelineId)
    );
    const stages = selectedPipeline?.stages || [];
    const statusField = analytics?.leads_status || analytics?.deals_status || {};

    return stages.map((stage: any) => ({
      stage_name: stage.stage_name,
      count: statusField[stage.stage_name] ?? 0,
    }));
  }

  getAllUsers() {
    const param = {
      client_id: this.localStorage.getClientId(),
    };

    this.userApi.findAllUsers(param).subscribe({
      next: (res: any) => {
        this.users = (res || []).map((user: any) => {
          return {
            ...user,
            name: [user.fname, user.last_name].join(' '),
            id: user.user_id
          };
        });
        this.isUserLoading = false;
      },
      error: (e: any) => {
        console.error(e);
        this.isUserLoading = false;
      },
    });
  }

  fnGetLeadAnalytics() {
    const param = this.buildLeadAnalyticsParams();
    this.leadLoading = true;
    this.api.getLeadAnalytics(param).subscribe({
      next: (res) => {
        if (res) {
          this.leadsAnalytics = res;
          this.statusCards = this.buildStatusCards(
            res,
            this.leadPipelineData,
            this.selectedLeadPipelineId
          );
          this.leadLoading = false;
          this.totalLeads = res.total_leads;
        }
      },
      error: () => {
        this.leadLoading = false;
        this.statusCards = [];
        this.leadsAnalytics = {
          platform_wise_leads: {},
          leads_types: {},
          leads_status: {},
          total_leads: '0',
        };
      }
    });
  }

  fnGetDealsAnalytics() {
    this.dealsAnalyticsLoading = true;
    const param = this.buildDealAnalyticsParams();

    this.dealsApi.getDealsAnalytics(param).subscribe({
      next: (res) => {
        if (res) {
          this.dealsAnalytics = res;
          this.dealStatusCards = this.buildStatusCards(
            res,
            this.dealPipelineData,
            this.selectedDealPipelineId
          );
          this.dealsAnalyticsLoading = false;
        }
      },
      error: () => {
        this.dealsAnalyticsLoading = false;
        this.dealStatusCards = [];
        this.dealsAnalytics = {
          deals_status: {},
          total_deals: '0',
          total_revenue: 0,
        };
      }
    });
  }

  fnChangeUser(event: any) {
    this.selectedUser = event;
    this.fnGetLeadAnalytics();
    this.fnGetDealsAnalytics();
  }

  fnChangeYear(event: any) {
    this.selectedYear = event;
    this.fnGetLeadAnalytics();
    this.fnGetDealsAnalytics();
  }

  private getTenantScopeParams(): Record<string, number> {
    return buildTenantScopeParams(this.localStorage);
  }

  private getSelectedYear(): number | string {
    return this.selectedYear || new Date().getFullYear();
  }

  private buildLeadAnalyticsParams(): Record<string, any> {
    const param: Record<string, any> = {
      ...this.getTenantScopeParams(),
      year: this.getSelectedYear(),
    };

    if (!this.isAdmin) {
      param['user_id'] = this.localStorage.getUserId();
    }

    if (this.selectedUser) {
      param['user_id'] = this.selectedUser;
    }

    if (this.selectedLeadPipelineId) {
      param['pipeline_id'] = this.selectedLeadPipelineId;
    }

    return param;
  }

  private buildDealAnalyticsParams(): Record<string, any> {
    const param: Record<string, any> = {
      ...this.getTenantScopeParams(),
      year: this.getSelectedYear(),
    };

    if (!this.isAdmin) {
      param['deal_owner'] = this.localStorage.getUserId();
    }

    if (this.selectedUser) {
      param['deal_owner'] = this.selectedUser;
    }

    if (this.selectedDealPipelineId) {
      param['pipeline_id'] = this.selectedDealPipelineId;
    }

    return param;
  }

  getTotalLeadCalls(): number {
    if (!this.leadsAnalytics?.lead_calls?.yearly) {
      return 0;
    }

    const yearlyData = this.leadsAnalytics.lead_calls.yearly;
    return Object.values(yearlyData).reduce((total: number, count: any) => total + (Number(count) || 0), 0);
  }

  getTotalLeadEmails(): number {
    if (!this.leadsAnalytics?.email_logs?.yearly) {
      return 0;
    }

    const yearlyData = this.leadsAnalytics.email_logs.yearly;
    return Object.values(yearlyData).reduce((total: number, count: any) => total + (Number(count) || 0), 0);
  }
}
